import { useEffect, useState } from 'react';
import * as logistaService from '@/services/logistaService';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

function statusBadge(status) {
  const map = {
    pending: { label: 'Pendente', cls: 'bg-amber-100 text-amber-700' },
    validated: { label: 'Validado', cls: 'bg-emerald-100 text-emerald-700' },
    expired: { label: 'Expirado', cls: 'bg-surfaceAlt text-muted' },
    cancelled: { label: 'Cancelado', cls: 'bg-red-100 text-red-600' },
  };
  const s = map[status] || { label: status, cls: 'bg-surfaceAlt text-muted' };
  return <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${s.cls}`}>{s.label}</span>;
}

export default function LogistaResgatesPage() {
  const [tab, setTab] = useState('pending');
  const [resgates, setResgates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qrInput, setQrInput] = useState('');
  const [validating, setValidating] = useState(false);
  const [validMsg, setValidMsg] = useState(null);

  const load = async (status) => {
    setLoading(true);
    try {
      const r = await logistaService.getResgates({ status: status === 'all' ? undefined : status });
      setResgates(r.data.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(tab); }, [tab]);

  const handleValidar = async (e) => {
    e.preventDefault();
    if (!qrInput.trim()) return;
    setValidating(true);
    setValidMsg(null);
    try {
      const r = await logistaService.validarResgate(qrInput.trim());
      setValidMsg({ type: 'success', text: `✅ Resgate validado! Oferta: ${r.data.data.offer}` });
      setQrInput('');
      await load(tab);
    } catch (err) {
      setValidMsg({ type: 'error', text: err.response?.data?.message || 'QR code inválido ou expirado.' });
    } finally {
      setValidating(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-serif text-2xl font-black text-fg">Resgates</h1>
        <p className="text-sm text-muted">Valida QR codes apresentados pelos jogadores.</p>
      </div>

      {/* QR Validator */}
      <div className="rounded-2xl border border-accent/20 bg-accent/5 p-5">
        <h2 className="mb-3 font-bold text-accent">Validar QR Code</h2>
        <form onSubmit={handleValidar} className="flex gap-3">
          <input
            type="text"
            value={qrInput}
            onChange={(e) => setQrInput(e.target.value)}
            placeholder="Cola aqui o código QR do jogador..."
            className="flex-1 rounded-xl border border-accent/30 bg-surface px-4 py-2.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
          />
          <button
            type="submit"
            disabled={validating || !qrInput.trim()}
            className="rounded-xl bg-accent px-5 py-2.5 text-sm font-bold text-white hover:bg-accent disabled:opacity-60"
          >
            {validating ? '...' : 'Validar'}
          </button>
        </form>
        {validMsg && (
          <p className={`mt-2 text-sm font-semibold ${validMsg.type === 'success' ? 'text-emerald-700' : 'text-red-600'}`}>
            {validMsg.text}
          </p>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-xl bg-surfaceAlt p-1 w-fit">
        {[
          { key: 'pending', label: 'Pendentes' },
          { key: 'validated', label: 'Validados' },
          { key: 'all', label: 'Todos' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${tab === t.key ? 'bg-surface text-accent shadow-sm' : 'text-muted hover:text-fg'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-accent border-t-transparent" />
        </div>
      ) : resgates.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-edge bg-surface py-16 text-center">
          <p className="text-4xl">🎫</p>
          <p className="mt-2 font-bold text-muted">Sem resgates {tab === 'pending' ? 'pendentes' : tab === 'validated' ? 'validados' : ''}</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {resgates.map((r) => (
            <div key={r.id} className="rounded-2xl border border-edge bg-surface p-4 shadow-sm">
              <div className="flex items-center gap-3">
                {r.player?.avatarUrl ? (
                  <img src={`${API_URL}${r.player.avatarUrl}`} alt="" className="h-10 w-10 rounded-full object-cover" />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/10 text-sm font-bold text-accent">
                    {r.player?.username?.[0]?.toUpperCase() || '?'}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-fg">{r.player?.displayName || r.player?.username || 'Jogador'}</p>
                    {statusBadge(r.status)}
                  </div>
                  <p className="truncate text-sm text-muted">{r.offer?.title}</p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-xs text-muted">{new Date(r.createdAt).toLocaleDateString('pt-PT')}</p>
                  {r.offer?.coinCost > 0 && (
                    <p className="text-xs font-semibold text-amber-600">{r.offer.coinCost} moedas</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
